import React from 'react'
import { useAuth } from '../contexts/AuthContext'
import { BOOKMAKER_LOGOS } from '../config/bookmakers'
import ToolTour, { useToolTour, openTour, InfoTip, MarkerKey, ProfitSketch, RecommendedFilters } from '../components/ToolTour'
import ArbCard from './ArbCard'

const C = {
  border: '#1b2538',
  borderStrong: '#26354f',
  text: '#f1f5f9',
  muted: '#94a3b8',
  dim: '#64748b',
  green: '#34d399',
  blue: '#60a5fa',
}

// Education's worked example (lesson 4.3), carried through slides 1 and 4
const SIDES = [
  { book: 'Sportsbet', pick: 'Over 22.5', odds: 2.15, stake: 100 },
  { book: 'Bet365', pick: 'Under 22.5', odds: 2.15, stake: 100 },
]
const TOTAL = SIDES.reduce((t, s) => t + s.stake, 0)
const RETURN = SIDES[0].odds * SIDES[0].stake
const PROFIT = RETURN - TOTAL

const panel = {
  boxSizing: 'border-box', borderRadius: '14px',
  background: 'linear-gradient(180deg, #0f1a2e 0%, #0a1222 100%)', border: `1px solid ${C.borderStrong}`,
}
const label = { fontSize: '10px', fontWeight: 700, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.08em' }

// Slide 1 visual: one bet on each side at two bookies, and the same return
// whichever side wins
function LockedIn({ isMobile }) {
  return (
    <div style={{ width: '100%', maxWidth: isMobile ? 'none' : '440px' }}>
      <div style={{ ...label, textAlign: 'center', marginBottom: isMobile ? '8px' : '10px' }}>Izak Rankine · Disposals</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: isMobile ? '8px' : '12px' }}>
        {SIDES.map((s, i) => (
          <div key={s.book} className="tt-rise" style={{ ...panel, padding: isMobile ? '10px' : '12px 14px', animationDelay: `${i * 0.12}s` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <img src={BOOKMAKER_LOGOS[s.book]} alt="" style={{ width: isMobile ? 22 : 26, height: isMobile ? 22 : 26, borderRadius: '6px', objectFit: 'cover', flexShrink: 0 }} />
              <span style={{ fontSize: isMobile ? '12.5px' : '13.5px', fontWeight: 700, color: C.text, whiteSpace: 'nowrap' }}>{s.pick}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: isMobile ? '8px' : '10px' }}>
              <span style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: 800, color: C.text, fontVariantNumeric: 'tabular-nums' }}>{s.odds.toFixed(2)}</span>
              <span style={{ fontSize: isMobile ? '12px' : '13px', fontWeight: 700, color: C.green }}>${s.stake} stake</span>
            </div>
          </div>
        ))}
      </div>
      <div className="tt-rise" style={{
        ...panel, marginTop: isMobile ? '10px' : '12px', overflow: 'hidden',
        border: '1px solid rgba(16,185,129,0.35)', animationDelay: '0.45s',
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
          {SIDES.map((s, i) => (
            <div key={s.pick} style={{ padding: isMobile ? '8px 10px' : '10px 14px', borderLeft: i ? `1px solid ${C.border}` : 'none' }}>
              <div style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600, color: C.muted }}>If {s.pick.split(' ')[0]} wins</div>
              <div style={{ fontSize: isMobile ? '15px' : '16px', fontWeight: 800, color: C.text, marginTop: '2px' }}>${RETURN.toFixed(0)} back</div>
            </div>
          ))}
        </div>
        <div className="tt-fade" style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isMobile ? '8px 10px' : '9px 14px',
          backgroundColor: 'rgba(16,185,129,0.10)', borderTop: '1px solid rgba(16,185,129,0.25)', animationDelay: '0.8s',
        }}>
          <span style={{ fontSize: isMobile ? '12px' : '13px', fontWeight: 700, color: C.green }}>Profit either way</span>
          <span style={{ fontSize: isMobile ? '15px' : '16px', fontWeight: 800, color: C.green }}>+${PROFIT.toFixed(0)} <span style={{ fontSize: '12px', fontWeight: 700 }}>({((PROFIT / TOTAL) * 100).toFixed(2)}%)</span></span>
        </div>
      </div>
    </div>
  )
}

// Slide 2 visual: arbitrage next to +EV, from Education lesson 4.3
const COMPARE = [
  { name: 'Arbitrage', color: C.green, rgb: '52,211,153', lines: [['+', 'Zero variance'], ['+', 'Profit on every bet'], ['-', 'Two bets to place each time']] },
  { name: '+EV betting', color: C.blue, rgb: '96,165,250', lines: [['+', 'More profit long term'], ['-', 'Swings and losing runs'], ['+', 'One bet each time']] },
]

function ArbVsEv({ isMobile }) {
  return (
    <div style={{ width: '100%', maxWidth: isMobile ? 'none' : '460px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: isMobile ? '8px' : '12px' }}>
      {COMPARE.map((c, ci) => (
        <div key={c.name} className="tt-rise" style={{
          ...panel, padding: isMobile ? '10px' : '14px 16px', border: `1px solid rgba(${c.rgb},0.35)`,
          background: `radial-gradient(120% 90% at 100% 0%, rgba(${c.rgb},0.10) 0%, rgba(${c.rgb},0) 60%), linear-gradient(180deg, #0f1a2e 0%, #0a1222 100%)`,
          animationDelay: `${ci * 0.15}s`,
        }}>
          <div style={{ fontSize: isMobile ? '14px' : '15px', fontWeight: 800, color: c.color }}>{c.name}</div>
          <div style={{ display: 'grid', gap: isMobile ? '6px' : '8px', marginTop: isMobile ? '8px' : '10px' }}>
            {c.lines.map(([sign, text]) => (
              <div key={text} style={{ display: 'flex', alignItems: 'flex-start', gap: '7px', fontSize: isMobile ? '11.5px' : '12.5px', fontWeight: 600, color: sign === '+' ? C.text : C.muted, lineHeight: 1.35 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={sign === '+' ? C.green : '#64748b'} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
                  {sign === '+' ? <polyline points="20 6 9 17 4 12" /> : <line x1="5" y1="12" x2="19" y2="12" />}
                </svg>
                {text}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// Slide 3. Total stake sits bottom right so its (i) opens inside the card.
function TotalStakeInfo() {
  return (
    <InfoTip label="What total stake means" title="Total stake" align="end">
      What you spend on each arb in total. It's split between the two bookies so you make the same profit whichever side wins, and the card shows each side's share.
    </InfoTip>
  )
}

const RECOMMENDED = [
  { label: 'Sports', value: 'All' },
  { label: 'Bookmakers', value: 'Your accounts' },
  { label: 'Profit %', value: '3% to 20%' },
  { label: 'Starts in', value: '0 to 24 hrs' },
  { label: 'Rounding', value: '$5' },
  { label: 'Total stake', value: 'To suit you', info: <TotalStakeInfo /> },
]

// Slide 4: the real card, carrying slide 1's arb
function OddsMovedInfo() {
  return (
    <InfoTip label="If the odds have moved" title="Odds moved?">
      Tap the arrow on the card to open the calculator and enter the new odds. If the profit is still positive, take it. If not, move on to the next one.
    </InfoTip>
  )
}

function CardGuide({ isMobile }) {
  // Commence times are naive UTC, the same as the API sends them
  const commence = new Date(Date.now() + 3 * 60 * 60 * 1000).toISOString().slice(0, 19)
  const opp = {
    sport: 'AFL', match: 'Adelaide Crows vs Gold Coast Suns', market_type: 'player_disposals', player_name: 'Izak Rankine',
    commence_time: commence, profit_percentage: (PROFIT / TOTAL) * 100,
    outcomes: SIDES.map(s => ({ bookmaker: s.book, selection: `Izak Rankine ${s.pick}`, odds: s.odds })),
  }
  return (
    // zoom (not transform) so the card's layout box shrinks too and the key
    // below stays in the stage. Two outcome rows make this card taller than
    // the other tools', hence the smaller zoom.
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ width: '100%', maxWidth: isMobile ? '236px' : '320px' }}>
        <div style={{ zoom: isMobile ? 0.68 : 0.78 }}>
          <ArbCard
            opp={opp}
            className="tt-card"
            oddsText={SIDES.map(s => s.odds.toFixed(2))}
            stakes={SIDES.map(s => s.stake)}
            markers={{ market: 1, books: 2, outcomes: 3, odds: 4, stakes: 5, profit: 6, track: 7 }}
          />
        </div>
      </div>
      <MarkerKey isMobile={isMobile} labels={['Market', 'Bookmaker', 'Outcome', 'Odds', 'Stake', 'Profit', 'Track']} />
    </div>
  )
}

function arbTourSlides(isMobile) {
  return [
    {
      title: 'How arbitrage works',
      body: `Bookies sometimes disagree on a price. When they do, you can back every outcome across two bookies and lock in a profit whatever happens. Here Sportsbet and Bet365 both pay ${SIDES[0].odds.toFixed(2)} on opposite sides, so $${SIDES[0].stake} on each returns $${RETURN.toFixed(0)} either way, a guaranteed $${PROFIT.toFixed(0)}.`,
      visual: <LockedIn isMobile={isMobile} />,
    },
    {
      title: 'Certainty over swings',
      body: 'Arbitrage has zero variance: every arb locks in a profit before the game starts. +EV makes more over the long run, because each arb hedges with a -EV side and takes two bets to place. If you\'d rather have certainty than swings, arbitrage is for you.',
      visual: <ArbVsEv isMobile={isMobile} />,
    },
    {
      title: 'Set your filters',
      body: 'Here\'s our recommended setup. Profit above 3% is worth your time, and very high percentages can flag your accounts. Once it\'s set, tap Save Current and set it as your default so it loads every time.',
      visual: <RecommendedFilters rows={RECOMMENDED} isMobile={isMobile} />,
    },
    {
      title: 'Using the arbitrage page',
      body: (
        <ol style={{ margin: 0, paddingLeft: '18px', display: 'grid', gap: '4px' }}>
          <li>Tap each bookie logo to open the game.</li>
          <li>Fill out both bet slips with the stakes shown, but don't confirm yet.</li>
          <li>Check the odds still match <OddsMovedInfo />, then place both straight after one another.</li>
          <li>Tap <strong style={{ color: C.text }}>+</strong> to track it in your Tracker.</li>
        </ol>
      ),
      visual: <CardGuide isMobile={isMobile} />,
    },
    {
      title: 'You\'re ready to go',
      body: 'Arbs disappear fast, sometimes in seconds, so act quickly when one appears. Every one you place locks in a profit, so your balance climbs steadily with no losing runs.',
      visual: <ProfitSketch isMobile={isMobile} steady headline="Profit on every arb" sub="No losing runs" />,
    },
  ]
}

export const openArbTour = () => openTour('arb')

export default function ArbTour({ isVisible, isMobile }) {
  const { hasAccess } = useAuth()
  const tour = useToolTour('arb', { isVisible })
  return (
    <ToolTour
      open={tour.open}
      onClose={tour.close}
      slides={arbTourSlides(isMobile)}
      eyebrow="Arbitrage"
      isMobile={isMobile}
      finalAction={hasAccess('arb') ? { label: 'Get into it' } : { label: 'Unlock with Sports Premium', onClick: () => { window.location.hash = '#/pricing' } }}
      learnMore={{ label: 'Learn more', onClick: () => window.dispatchEvent(new CustomEvent('bc-navigate', { detail: 'education' })) }}
    />
  )
}
